import { ApiError } from "@/lib/api-client";

// Maps a thrown super_admin legacy-cert error to a single (PHI-free) message. Branches
// ONLY on the structured error.code / error.status — never the server's message/details
// text — and always renders our own copy (mirror of profile/legacy-cert-error.ts).
//
// [Source: common/exceptions.py — LEGACY-400 (reject reason required, 400, Story 16.5) /
//  LEGACY-404 (cert not found) / LEGACY-410 (already decided, 409) / LEGACY-503 (storage
//  unavailable); the capability gate's bare 403 (InsufficientPermissionsException);
//  api-client NETWORK_ERROR (status 0).]

const REASON_REQUIRED =
  "Enter a reason for the rejection before submitting.";
const NOT_FOUND =
  "That certificate could not be found. It may have already been decided — refresh the queue.";
const ALREADY_DECIDED =
  "This certificate has already been decided. Refresh the queue to see the latest.";
const UNAVAILABLE =
  "The certificate service is temporarily unavailable. Please try again in a moment.";
const FORBIDDEN = "You don't have permission to verify certificates.";
const NETWORK = "We couldn't reach the server. Check your connection and try again.";
const GENERIC = "Something went wrong. Please try again.";

/** True when the error means the queue is stale and should be refetched. */
export function isStaleCertError(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    (error.code === "LEGACY-404" ||
      error.code === "LEGACY-410" ||
      error.status === 404 ||
      error.status === 409)
  );
}

/** Map an approve / reject / download failure to a short, generic, PHI-free message.
 *
 * Covers the reject path too (Story 16.5): LEGACY-400 means the server rejected an
 * empty reason — surfaced here as the server-side defense behind the client-side
 * disabled-submit guard. Branching is on the structured code/status only. */
export function toLegacyCertAdminErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === "LEGACY-400") return REASON_REQUIRED;
    if (error.code === "LEGACY-404" || error.status === 404) return NOT_FOUND;
    if (error.code === "LEGACY-410" || error.status === 409) return ALREADY_DECIDED;
    if (error.code === "LEGACY-503" || error.status === 503) return UNAVAILABLE;
    if (error.status === 403) return FORBIDDEN;
    if (error.code === "NETWORK_ERROR" || error.status === 0) return NETWORK;
  }
  return GENERIC;
}
